/**
 * Feature flags, read from the build's environment.
 *
 * The flags arrive as secrets in the Azure key vault the deploy workflow reads
 * (`dhs-kv-client-dev`, `dhs-kv-client-staging`), named with dashes there and
 * with underscores here — `VITE-FF-DISABLE-INBOX` becomes
 * `VITE_FF_DISABLE_INBOX`. The workflow has been fetching three of them for a
 * while and nothing in the app ever read one, so turning a feature off meant
 * changing the secret and watching nothing happen.
 *
 * Only the exact string "true" disables a feature. Anything else — unset, an
 * empty string, "false", "0", "TRUE" with the wrong case — leaves it on, so a
 * mistyped or missing secret can never take a feature away by accident. This
 * is the SP app's rule, and the two apps read the same vault family, so they
 * must agree on what a value means.
 */

/** Canonical feature-flag semantics: only the string "true" disables a feature. */
export const isFeatureDisabled = (value?: string): boolean => value?.trim() === 'true';

/** The secret names as the key vault spells them, for the deploy workflow. */
export const canonicalFeatureFlagSecrets = [
  'VITE-FF-DISABLE-CHECKLISTS',
  'VITE-FF-DISABLE-INBOX',
  'VITE-FF-DISABLE-RECURRING-SCHEDULE',
  'VITE-FF-DISABLE-AI-ASSISTANT',
] as const;

type FeatureFlagEnvironment = Partial<
  Record<
    | 'VITE_FF_DISABLE_CHECKLISTS'
    | 'VITE_FF_DISABLE_INBOX'
    | 'VITE_FF_DISABLE_RECURRING_SCHEDULE'
    | 'VITE_FF_DISABLE_AI_ASSISTANT',
    string
  >
>;

export const createFeatureFlags = (environment: FeatureFlagEnvironment) => ({
  isChecklistsDisabled: () => isFeatureDisabled(environment.VITE_FF_DISABLE_CHECKLISTS),
  isInboxDisabled: () => isFeatureDisabled(environment.VITE_FF_DISABLE_INBOX),
  isRecurringScheduleDisabled: () =>
    isFeatureDisabled(environment.VITE_FF_DISABLE_RECURRING_SCHEDULE),
  isAiAssistantDisabled: () => isFeatureDisabled(environment.VITE_FF_DISABLE_AI_ASSISTANT),
});

export const canonicalFeatureFlags = createFeatureFlags(import.meta.env);
